import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../axiosConfig";

const AddPricingPopup = ({ onClose, refreshPricingPlans }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modules, setModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [userPlans, setUserPlans] = useState([]);
  const [userPlanValues, setUserPlanValues] = useState({});
  const [userPlanAdminValues, setUserPlanAdminValues] = useState({});
  const [userPlanServerCostValues, setUserPlanServerCostValues] = useState({});
  const [priceAllocatedFrom, setPriceAllocatedFrom] = useState("");
  const [priceAllocatedTo, setPriceAllocatedTo] = useState("");
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState({});
  const [moduleFeatureAccessCodes, setModuleFeatureAccessCodes] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const sp = localStorage.getItem("selectedProduct");
    if (sp) {
      try {
        setSelectedProduct(JSON.parse(sp));
      } catch {
        toast.error("Invalid product data");
      }
    }
  }, []);

  useEffect(() => {
    const fetchModulesForProduct = async () => {
      if (!selectedProduct?.productId) return;
      try {
        const response = await axiosInstance.get(
          `/modules/${selectedProduct.productId}/products`,
        );
        if (response.data.status === 200) {
          const modulesData = Array.isArray(response.data.data)
            ? response.data.data
            : response.data.data
              ? [response.data.data]
              : [];
          setModules(modulesData);
          const featureAccessCodes = {};
          modulesData.forEach((module) => {
            if (module.moduleFeatureAccessIds) {
              featureAccessCodes[module.moduleId] =
                module.moduleFeatureAccessIds.map((fa) => ({
                  id: fa.moduleFeatureAccessId,
                  code: fa.featureAccessCode,
                  name: fa.featureName,
                }));
            }
          });
          setModuleFeatureAccessCodes(featureAccessCodes);
        }
      } catch {
        toast.error("Failed to load modules");
      }
    };
    fetchModulesForProduct();
  }, [selectedProduct]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, userPlansRes] = await Promise.all([
          axiosInstance.get("/subscription-plans"),
          axiosInstance.get("/user-plans"),
        ]);
        if (plansRes.data.status === 200)
          setSubscriptionPlans(plansRes.data.data);
        if (userPlansRes.data.status === 200)
          setUserPlans(userPlansRes.data.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchData();
  }, []);

  const handleUserPlanValueChange = (planName, userPlanId, value) => {
    setUserPlanValues((prev) => ({
      ...prev,
      [planName]: { ...prev[planName], [userPlanId]: value },
    }));
  };
  const handleUserPlanAdminValueChange = (planName, userPlanId, value) => {
    setUserPlanAdminValues((prev) => ({
      ...prev,
      [planName]: { ...prev[planName], [userPlanId]: value },
    }));
  };
  const handleUserPlanServerCostValueChange = (planName, userPlanId, value) => {
    setUserPlanServerCostValues((prev) => ({
      ...prev,
      [planName]: { ...prev[planName], [userPlanId]: value },
    }));
  };

  const toggleFeature = (planName, featureId) => {
    setSelectedFeatures((prev) => {
      const current = prev[planName] || {
        moduleFeatureIDs: [],
        moduleFeatureAccessIDs: [],
      };
      if (featureId.moduleFeatureId) {
        const list = [...current.moduleFeatureIDs];
        const idx = list.indexOf(featureId.moduleFeatureId);
        idx !== -1 ? list.splice(idx, 1) : list.push(featureId.moduleFeatureId);
        return { ...prev, [planName]: { ...current, moduleFeatureIDs: list } };
      }
      if (featureId.featureAccessCode) {
        const accessCodeObj = moduleFeatureAccessCodes[selectedModuleId]?.find(
          (f) => f.code === featureId.featureAccessCode,
        );
        if (!accessCodeObj) return prev;
        const list = [...current.moduleFeatureAccessIDs];
        const idx = list.findIndex((f) => f.id === accessCodeObj.id);
        idx > -1 ? list.splice(idx, 1) : list.push(accessCodeObj);
        return {
          ...prev,
          [planName]: { ...current, moduleFeatureAccessIDs: list },
        };
      }
      return prev;
    });
  };

  const toggleSelectAllFeatures = () => {
    if (!selectedModule || !selectedPlanId) return;
    const allIds =
      selectedModule.moduleFeatures?.map((f) => f.moduleFeatureId) || [];
    setSelectedFeatures((prev) => {
      const current = prev[selectedPlanId]?.moduleFeatureIDs || [];
      return {
        ...prev,
        [selectedPlanId]: {
          ...prev[selectedPlanId],
          moduleFeatureIDs: current.length === allIds.length ? [] : allIds,
          moduleFeatureAccessIDs:
            prev[selectedPlanId]?.moduleFeatureAccessIDs || [],
        },
      };
    });
  };

  const toggleSelectAllAccessCodes = () => {
    if (!selectedModule || !selectedPlanId) return;
    const allCodes = moduleFeatureAccessCodes[selectedModuleId] || [];
    setSelectedFeatures((prev) => {
      const current = prev[selectedPlanId]?.moduleFeatureAccessIDs || [];
      return {
        ...prev,
        [selectedPlanId]: {
          ...prev[selectedPlanId],
          moduleFeatureIDs: prev[selectedPlanId]?.moduleFeatureIDs || [],
          moduleFeatureAccessIDs:
            current.length === allCodes.length ? [] : allCodes,
        },
      };
    });
  };

  const copyPricingToAll = (planName, sourceUserPlanId) => {
    const amount = userPlanValues[planName]?.[sourceUserPlanId] || "";
    const adminAmount = userPlanAdminValues[planName]?.[sourceUserPlanId] || "";
    const serverCost =
      userPlanServerCostValues[planName]?.[sourceUserPlanId] || "";
    userPlans.forEach((up) => {
      handleUserPlanValueChange(planName, up.userPlanId, amount);
      handleUserPlanAdminValueChange(planName, up.userPlanId, adminAmount);
      handleUserPlanServerCostValueChange(planName, up.userPlanId, serverCost);
    });
  };

  const savePricingData = async () => {
    setLoading(true);
    const pricingData = {
      moduleId: selectedModuleId,
      subscriptionPlans: Object.keys(selectedFeatures).map((planName) => {
        const subscriptionPlan = subscriptionPlans.find(
          (p) => p.planName === planName,
        );
        const moduleFeatureIDs =
          selectedFeatures[planName]?.moduleFeatureIDs || [];
        const moduleFeatureAccessIDs =
          selectedFeatures[planName]?.moduleFeatureAccessIDs || [];
        return {
          subscriptionPlanId: subscriptionPlan?.planId,
          planName,
          planSequence: subscriptionPlan?.planSequence || 0,
          subscriptionType: subscriptionPlan?.subscriptionType,
          userPlans: userPlans.map((up) => ({
            userPlanId: up.userPlanId,
            minUser: up.minimumUser,
            maxUser: up.maximumUser,
            planAmount: userPlanValues[planName]?.[up.userPlanId] || 0,
            planAmountPerAdmin:
              userPlanAdminValues[planName]?.[up.userPlanId] || 0,
            serverCostPercenatgePerUser:
              userPlanServerCostValues[planName]?.[up.userPlanId] || 0,
            priceAllocatedFrom,
            priceAllocatedTo,
          })),
          moduleFeatureIDs,
          moduleFeatureAccessIDs: moduleFeatureAccessIDs.map((a) => a.id),
        };
      }),
      priceAllocatedFrom,
      priceAllocatedTo,
    };
    try {
      const response = await axiosInstance.post(
        "/module-pricing-plans/create",
        pricingData,
      );
      if (response.status === 200) {
        toast.success("Pricing plan saved successfully!");
        onClose();
        refreshPricingPlans();
      }
    } catch {
      toast.error("Failed to save pricing plan.");
    } finally {
      setLoading(false);
    }
  };

  const selectedModule = modules?.find((m) => m.moduleId === selectedModuleId);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-3 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-5 py-3 flex items-center justify-between rounded-t-xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-white">
              Add New Pricing Plan
            </h2>
            {selectedProduct && (
              <span className="bg-white/20 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                {selectedProduct.productName}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4">
            {/* Row 1: Module + Date Range */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 tracking-wide mb-1">
                  Module
                </label>
                <select
                  value={selectedModuleId || ""}
                  onChange={(e) => setSelectedModuleId(Number(e.target.value))}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">— Select Module —</option>
                  {modules.map((m) => (
                    <option key={m.moduleId} value={m.moduleId}>
                      {m.moduleName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 tracking-wide mb-1">
                  Price From
                </label>
                <input
                  type="date"
                  value={priceAllocatedFrom}
                  onChange={(e) => setPriceAllocatedFrom(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 tracking-wide mb-1">
                  Price To
                </label>
                <input
                  type="date"
                  value={priceAllocatedTo}
                  onChange={(e) => setPriceAllocatedTo(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>

            {/* Row 2: Subscription Plans */}
            {selectedModuleId && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 tracking-wide mb-1.5">
                  Subscription Plan
                </label>
                <div className="flex flex-wrap gap-2">
                  {subscriptionPlans.map((plan) => (
                    <button
                      key={plan.planId}
                      onClick={() => setSelectedPlanId(plan.planName)}
                      className={`px-3 py-1.5 text-sm rounded-lg border-2 font-medium transition-all ${
                        selectedPlanId === plan.planName
                          ? "border-green-500 bg-green-50 text-green-700"
                          : "border-gray-200 bg-white hover:border-gray-300 text-gray-600"
                      }`}
                    >
                      {plan.planName}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Row 3: Features + Access Codes + User Plans — 3 columns */}
            {selectedModuleId && selectedPlanId && selectedModule && (
              <div className="grid grid-cols-3 gap-4 min-h-0">
                {/* Module Features */}
                <div className="bg-gray-50 rounded-lg border border-gray-200 flex flex-col">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
                    <span className="text-xs font-bold text-gray-700 tracking-wide">
                      Module Features
                    </span>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-3.5 h-3.5 text-green-600 rounded"
                        checked={
                          selectedModule.moduleFeatures?.length > 0 &&
                          selectedFeatures[selectedPlanId]?.moduleFeatureIDs
                            ?.length === selectedModule.moduleFeatures.length
                        }
                        onChange={toggleSelectAllFeatures}
                      />
                      <span className="text-xs text-gray-500">All</span>
                    </label>
                  </div>
                  <div className="overflow-y-auto p-2 flex-1">
                    <div className="grid grid-cols-3 gap-1">
                      {selectedModule.moduleFeatures?.map((feature) => (
                        <label
                          key={feature.moduleFeatureId}
                          className="flex items-start gap-1.5 p-1.5 rounded-md hover:bg-white cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 w-3.5 h-3.5 text-green-600 rounded flex-shrink-0"
                            checked={selectedFeatures[
                              selectedPlanId
                            ]?.moduleFeatureIDs?.includes(
                              feature.moduleFeatureId,
                            )}
                            onChange={() =>
                              toggleFeature(selectedPlanId, {
                                moduleFeatureId: feature.moduleFeatureId,
                              })
                            }
                          />
                          <p
                            className="text-xs font-medium text-gray-800 leading-snug truncate"
                            title={feature.featureName}
                          >
                            {feature.featureName}
                          </p>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Feature Access Codes */}
                <div className="bg-gray-50 rounded-lg border border-gray-200 flex flex-col">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
                    <span className="text-xs font-bold text-gray-700 tracking-wide">
                      Access Codes
                    </span>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-3.5 h-3.5 text-purple-600 rounded"
                        checked={
                          moduleFeatureAccessCodes[selectedModuleId]?.length >
                            0 &&
                          selectedFeatures[selectedPlanId]
                            ?.moduleFeatureAccessIDs?.length ===
                            moduleFeatureAccessCodes[selectedModuleId]?.length
                        }
                        onChange={toggleSelectAllAccessCodes}
                      />
                      <span className="text-xs text-gray-500">All</span>
                    </label>
                  </div>
                  <div className="overflow-y-auto p-2 space-y-1 flex-1">
                    {selectedModule?.moduleFeatureAccessIds?.map((access) => (
                      <label
                        key={access.moduleFeatureAccessId}
                        className="flex items-center gap-2 p-2 rounded-md hover:bg-white cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 text-purple-600 rounded flex-shrink-0"
                          checked={selectedFeatures[
                            selectedPlanId
                          ]?.moduleFeatureAccessIDs?.some(
                            (f) => f.code === access.featureAccessCode,
                          )}
                          onChange={() =>
                            toggleFeature(selectedPlanId, {
                              featureAccessCode: access.featureAccessCode,
                            })
                          }
                        />
                        <span className="text-xs font-medium text-gray-700 font-mono">
                          {access.featureAccessCode}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* User Plans Pricing */}
                <div className="bg-gray-50 rounded-lg border border-gray-200 flex flex-col">
                  <div className="px-3 py-2 border-b border-gray-200">
                    <span className="text-xs font-bold text-gray-700 tracking-wide">
                      User Plans — {selectedPlanId}
                    </span>
                  </div>
                  <div className="overflow-y-auto p-2 space-y-2 flex-1">
                    {userPlans.map((userPlan) => (
                      <div
                        key={userPlan.userPlanId}
                        className="bg-white rounded-lg border border-gray-200 p-2"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-semibold text-gray-700">
                            {userPlan.minimumUser}–{userPlan.maximumUser} Users
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              copyPricingToAll(
                                selectedPlanId,
                                userPlan.userPlanId,
                              )
                            }
                            className="text-xs text-green-600 hover:text-green-700 font-medium hover:underline"
                          >
                            Copy all
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-1.5">
                          <div>
                            <label className="block text-xs text-gray-400 mb-0.5">
                              Amount (₹)
                            </label>
                            <input
                              type="number"
                              value={
                                userPlanValues[selectedPlanId]?.[
                                  userPlan.userPlanId
                                ] || ""
                              }
                              onChange={(e) =>
                                handleUserPlanValueChange(
                                  selectedPlanId,
                                  userPlan.userPlanId,
                                  e.target.value,
                                )
                              }
                              placeholder="0"
                              className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-0.5">
                              Per Admin (₹)
                            </label>
                            <input
                              type="number"
                              value={
                                userPlanAdminValues[selectedPlanId]?.[
                                  userPlan.userPlanId
                                ] || ""
                              }
                              onChange={(e) =>
                                handleUserPlanAdminValueChange(
                                  selectedPlanId,
                                  userPlan.userPlanId,
                                  e.target.value,
                                )
                              }
                              placeholder="0"
                              className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-0.5">
                              Server %
                            </label>
                            <input
                              type="number"
                              value={
                                userPlanServerCostValues[selectedPlanId]?.[
                                  userPlan.userPlanId
                                ] || ""
                              }
                              onChange={(e) =>
                                handleUserPlanServerCostValueChange(
                                  selectedPlanId,
                                  userPlan.userPlanId,
                                  e.target.value,
                                )
                              }
                              placeholder="1%"
                              min="0"
                              max="100"
                              className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0 rounded-b-xl bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            className={`px-5 py-1.5 text-sm rounded-lg font-semibold transition-all text-white flex items-center gap-2 ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 active:bg-green-800"
            }`}
            onClick={savePricingData}
            disabled={loading}
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-3.5 w-3.5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving...
              </>
            ) : (
              <>
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Save Pricing Plan
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPricingPopup;
